<template>
  <transition
    name="transition"
    :enter-from-class="$style['transition-enter-from']"
    :enter-active-class="$style['transition-enter-active']"
    :leave-to-class="$style['transition-leave-to']"
    :leave-active-class="$style['transition-leave-active']"
    mode="out-in"
    @enter="handleChange"
  >
    <transition-group
      name="transition"
      tag="ul"
      :class="$style.list"
      :enter-from-class="$style['transition-enter-from']"
      :enter-active-class="$style['transition-enter-active']"
      :leave-to-class="$style['transition-leave-to']"
      :leave-active-class="$style['transition-leave-active']"
      @enter="handleChange"
    >
      <li
        :class="[$style.list__item, $style[`list__item--${item.message.type}`]]"
        v-for="item in items"
        :key="item.message.id"
      >
        <ChatMessage
          v-if="item.message.type === 'normal'"
          :message="item.message"
          :userType="item.userType"
          :personalityType="personalityType"
          :isReadByUser="item.isReadByUser"
          @clickUserThumbnail="emit('clickUserThumbnail')"
        />
        <ChatMessageLivechat
          v-else-if="item.message.type === 'livechat'"
          :message="item.message"
          :user="match.user"
        />
        <ChatMessageReview
          v-else-if="item.message.type === 'review'"
          :message="item.message"
          :user="match.user"
          @clickUserThumbnail="emit('clickUserThumbnail')"
        />
        <ChatMessageSelections
          v-if="item.message.type === 'selections'"
          :message="item.message"
          :userType="item.userType"
          :personalityType="personalityType"
          :isReadByUser="item.isReadByUser"
          @clickUserThumbnail="emit('clickUserThumbnail')"
        />
        <ChatMessageAnswer
          v-if="item.message.type === 'answer'"
          :message="item.message"
          :userType="item.userType"
          :personalityType="personalityType"
          :isReadByUser="item.isReadByUser"
          @clickUserThumbnail="emit('clickUserThumbnail')"
        />
        <ChatMessageImage
          v-if="item.message.type === 'image'"
          :message="item.message"
          :userType="item.userType"
          :personalityType="personalityType"
          :isReadByUser="item.isReadByUser"
          @clickUserThumbnail="emit('clickUserThumbnail')"
        />
        <ChatMessageBot
          v-if="item.message.type === 'bot_question'"
          :message="item.message"
          :selections="item.message.selections"
        />
        <template v-for="a in item.message.attachments">
          <BookingRequest
            v-if="a.type === 'bookingRequest'"
            :attachment="a"
            :hasBeenRead="item.isReadByUser"
            :date="item.message.createdAt"
            :userType="item.userType"
            :key="`booking-request-${a.type}:${a.content.id}`"
          />
          <BookingAnswer
            v-else-if="a.type === 'bookingAnswer'"
            :attachment="a"
            :hasBeenRead="item.isReadByUser"
            :date="item.message.createdAt"
            :userType="item.userType"
            :key="`booking-answer-${a.type}:${a.content.id}`"
          />
        </template>
      </li>
      <li
        :class="$style.list__item"
        v-show="isUserTyping"
        key="__typingIndicator"
      >
        <ChatMessageUserTypingIndicator :personalityType="personalityType" />
      </li>
    </transition-group>
  </transition>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useThrottleFn } from '@vueuse/core'

import ChatMessage from './ChatMessage.vue'
import ChatMessageReview from './ChatMessageReview.vue'
import ChatMessageLivechat from './ChatMessageLivechat.vue'
import ChatMessageUserTypingIndicator from './ChatMessageUserTypingIndicator.vue'
import ChatMessageAnswer from './ChatMessageAnswer.vue'
import ChatMessageSelections from './ChatMessageSelections.vue'
import ChatMessageBot from './ChatMessageBot.vue'
import ChatMessageImage from './ChatMessageImage.vue'
import BookingRequest from '@/components/organisms/ChatMessageList/BookingRequest.vue'
import BookingAnswer from '@/components/organisms/ChatMessageList/BookingAnswer.vue'

import type { AnyMessage, Match, PersonalityType } from '@/types/app'
import type { ChatMessageUserType } from './ChatMessageUserType'

const CHANGE_THROTTLE_DELAY = 100

type MessageItem = {
  message: AnyMessage
  isReadByUser: boolean
  userType: ChatMessageUserType
}

const props = withDefaults(
  defineProps<{
    messages: readonly AnyMessage[]
    match: Match
    isUserTyping?: boolean
  }>(),
  {
    isUserTyping: false,
  }
)

const emit = defineEmits<{
  (event: 'change', changeThrottleDelay: number): void
  (event: 'clickUserThumbnail'): void
}>()

const items = computed(() =>
  props.messages.map((message): MessageItem => {
    return {
      message,
      isReadByUser: isReadByUser(props.match, message),
      userType: message.sender.kind,
    }
  })
)

const personalityType = computed<Maybe<PersonalityType>>(
  () => props.match.user.diagnosisResult?.personality.type
)

const handleChange = useThrottleFn(() => emit('change', CHANGE_THROTTLE_DELAY))

const isReadByUser = (match: Match, message: AnyMessage): boolean => {
  if (match.userLatestReadMessageGUID === undefined) {
    return false
  }
  return match.userLatestReadMessageGUID >= message.guid
}
</script>

<style lang="scss" module>
.transition {
  // stylelint-disable selector-class-pattern
  &-enter-active,
  &-leave-active {
    transition:
      transform $transition-base,
      opacity $transition-base;
  }

  &-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }

  &-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }
  // stylelint-enable selector-class-pattern
}

.list {
  padding: 24px;

  &__item {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }

    &--review {
      &:nth-child(n + 2) {
        margin-top: 40px;
      }
    }
  }
}
</style>
